import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { ARTICLES, CATEGORIES } from "@/data/articles";
import { ArrowUpRight } from "lucide-react";

const CATEGORY_COLORS = {
  "Password Recovery": "text-[#FFD700] border-[#FFD700]/40",
  "Wallet Reviews": "text-[#10B981] border-[#10B981]/40",
  DeFi: "text-[#7DD3FC] border-[#7DD3FC]/40",
};

export default function KnowledgeCenter() {
  const { t } = useLang();
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return ARTICLES;
    return ARTICLES.filter((a) => a.category === active);
  }, [active]);

  return (
    <section
      id="knowledge-center"
      data-testid="knowledge-center"
      className="py-16 sm:py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 sm:mb-10">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-3">
              {t("kc.tag")}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-3 max-w-2xl">
              {t("kc.title")}
            </h2>
            <p className="text-white/60 max-w-2xl text-sm sm:text-base">
              {t("kc.subtitle")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const isActive = c === active;
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`kc-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setActive(c)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
                    isActive
                      ? "bg-[#FFD700] text-black border-[#FFD700]"
                      : "border-white/20 text-white/60 hover:text-white hover:border-white/40"
                  }`}
                >
                  {c === "All" ? t("kc.all") : c}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filtered.map((a) => {
            const catCls =
              CATEGORY_COLORS[a.category] || "text-white/60 border-white/20";
            return (
              <Link
                key={a.slug}
                to={`/knowledge/${a.slug}`}
                data-testid={`kc-article-${a.slug}`}
                className="group bg-[#0A0A0A] border border-white/10 hover:border-[#FFD700]/40 p-6 flex flex-col transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${catCls}`}
                  >
                    {a.category}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#FFD700] transition-colors" />
                </div>
                <h3 className="font-display text-lg font-black tracking-tight mb-2 leading-snug">
                  {a.title}
                </h3>
                <p className="text-xs text-white/60 leading-relaxed">
                  {a.excerpt}
                </p>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-white/40">
                  {a.readMinutes} min read · {a.date}
                </div>
                <span className="mt-1 text-xs font-mono uppercase tracking-widest text-[#FFD700] opacity-0 group-hover:opacity-100 transition-opacity">
                  {t("kc.read")} →
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
